import {useState, useMemo} from 'react'
import {User, Mail, Shield, Key, Clock, UserCircle, ChevronRight, Check} from 'lucide-react'
import {useAuth} from '@/contexts/auth-context'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from '@/components/ui/collapsible'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {useI18n} from '@/i18n'
import {Stack} from '@/components/shared/Stack'
import {SectionContainer} from '@/components/shared/SectionContainer'
import {routes} from '@/lib/routes'
import {PageBackButton} from '@/components/shared/PageBackButton'
import {InlineRow} from '@/components/shared/InlineRow'
import {CardSection} from '@/components/shared/CardSection'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {IconText} from '@/components/shared/IconText'
import {PageSectionHeader} from '@/components/shared/PageSectionHeader'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {DetailList} from '@/components/shared/DetailList'
import {DetailField} from '@/components/shared/DetailField'
import {Icon} from '@/components/shared/Icon'

const ACTION_ORDER: readonly string[] = ['view', 'create', 'update', 'delete']

function capitalize(value: string): string {
    return value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1)
}

function usePermissionsMatrix(permissionStrings: string[]) {
    return useMemo(() => {
        const resourceSet = new Set<string>()
        const actionSet = new Set<string>()
        const permSet = new Set<string>()

        permissionStrings.forEach((perm) => {
            const [resource, action] = perm.split(':')
            resourceSet.add(resource)
            actionSet.add(action)
            permSet.add(perm)
        })

        const sortedActions = Array.from(actionSet).sort((a, b) => {
            const aIdx = ACTION_ORDER.indexOf(a)
            const bIdx = ACTION_ORDER.indexOf(b)
            if (aIdx === -1 && bIdx === -1) return a.localeCompare(b)
            if (aIdx === -1) return 1
            if (bIdx === -1) return -1
            return aIdx - bIdx
        })

        return {
            resources: Array.from(resourceSet).sort(),
            actions: sortedActions,
            permSet,
        }
    }, [permissionStrings])
}

export function Profile() {
    const i18n = useI18n()
    usePageTitle(i18n.profile.pageTitle)
    const {user, logout} = useAuth()
    const [permissionsOpen, setPermissionsOpen] = useState(false)
    const [resourceFilter, setResourceFilter] = useState('')
    const {resources, actions, permSet} = usePermissionsMatrix(user?.permissions ?? [])

    if (!user) {
        return null
    }

    return (
        <SectionContainer>
            <PageBackButton/>

            <PageSectionHeader
                title={String(i18n.profile.userProfile)}
                icon={User}
                description={String(i18n.profile.accountInfo)}
                actions={
                    <Button variant="destructive" onClick={logout} data-testid="btn-logout">
                        {i18n.common.logout}
                    </Button>
                }
            />

            <DetailGrid columns={2}>
                {/* Basic Info Card */}
                <CardSection data-testid="section-basic-info" icon={User} title={i18n.profile.basicInfo as string} description={i18n.profile.accountDetails as string}>
                    <DetailList>
                        <DetailField
                            label={<IconText icon={Mail} size="sm">{i18n.profile.email}</IconText>}
                            value={user.email || i18n.profile.notProvided}
                        />
                        <DetailField
                            label={i18n.profile.displayName}
                            value={user.name || i18n.profile.notProvided}
                        />
                    </DetailList>
                </CardSection>

                {/* IAM Identity Card */}
                <CardSection data-testid="section-iam-identity" icon={UserCircle} title={i18n.profile.iamIdentity as string} description={i18n.profile.linkedIdentity as string}>
                    <Stack>
                        {user.identityId ? (
                            <>
                                <DetailField
                                    label={i18n.common.status}
                                    value={<Badge variant={BadgeVariant.SUCCESS}>{i18n.profile.linked}</Badge>}
                                />
                                <TextLink
                                    to={routes.identityManagement.identities.detail(user.identityId)}
                                    testId="link-identity-details"
                                >
                                    {i18n.profile.viewIdentityDetails} {'\u2192'}
                                </TextLink>
                            </>
                        ) : (
                            <MutedText block>
                                {i18n.profile.noIdentityLinked}
                                <MutedText size="sm" block className="mt-2">{i18n.profile.contactAdmin}</MutedText>
                            </MutedText>
                        )}
                    </Stack>
                </CardSection>

                {/* Roles & Permissions Card */}
                <Card className="md:col-span-2">
                    <CardHeader>
                        <CardTitle data-testid="card-title-roles-permissions">
                            <IconText icon={Shield} size="md">{i18n.profile.rolesAndPermissions}</IconText>
                        </CardTitle>
                        <CardDescription>{i18n.profile.rolesAndPermissionsDesc}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Stack gap="xs">
                            <MutedText size="sm" weight="medium">{i18n.profile.roles}</MutedText>
                            <InlineRow wrap gap="md">
                                {user.roles.length > 0 ? (
                                    user.roles.map((role) => (
                                        <Badge key={role} variant="secondary" className="text-sm">
                                            {role}
                                        </Badge>
                                    ))
                                ) : (
                                    <MutedText>{i18n.common.noRolesAssigned}</MutedText>
                                )}
                            </InlineRow>
                        </Stack>
                        <Collapsible open={permissionsOpen} onOpenChange={setPermissionsOpen}>
                            <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="sm" className="gap-2 -ml-2" data-testid="btn-toggle-permissions">
                                    <Icon icon={ChevronRight} size="md" rotated={permissionsOpen}/>
                                    {permissionsOpen ? i18n.profile.hidePermissions : i18n.profile.showPermissions}
                                    <Badge variant="outline" className="ml-1">
                                        {i18n.profile.permissionCount({count: String(user.permissions.length)})}
                                    </Badge>
                                </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                {user.permissions.length > 0 ? (
                                    <Stack gap="sm" className="mt-2">
                                        <Input
                                            placeholder={i18n.common.filterByResource}
                                            value={resourceFilter}
                                            onChange={(e) => setResourceFilter(e.target.value)}
                                            className="max-w-xs"
                                            data-testid="input-filter-permissions"
                                        />
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead data-testid="col-resource">{i18n.profile.resource}</TableHead>
                                                    {actions.map((action) => (
                                                        <TableHead key={action} className="text-center w-24" data-testid={`col-action-${action}`}>
                                                            {capitalize(action)}
                                                        </TableHead>
                                                    ))}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {resources.filter((r) => r.includes(resourceFilter.toLowerCase())).map((resource) => (
                                                    <TableRow key={resource}>
                                                        <TableCell className="font-medium">{resource}</TableCell>
                                                        {actions.map((action) => (
                                                            <TableCell key={action} className="text-center">
                                                                {permSet.has(`${resource}:${action}`) && (
                                                                    <Icon icon={Check} size="md" className="mx-auto text-status-success"/>
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Stack>
                                ) : (
                                    <MutedText size="sm" block className="mt-2">{i18n.profile.noPermissions}</MutedText>
                                )}
                            </CollapsibleContent>
                        </Collapsible>
                    </CardContent>
                </Card>

                {/* Session Info Card */}
                <Card className="md:col-span-2">
                    <CardHeader>
                        <CardTitle data-testid="card-title-session-info">
                            <IconText icon={Key} size="md">{i18n.profile.sessionInfo}</IconText>
                        </CardTitle>
                        <CardDescription>{i18n.profile.sessionDetails}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <DetailList>
                            <DetailField
                                label={<IconText icon={Clock} size="sm">{i18n.profile.sessionStatus}</IconText>}
                                value={<Badge variant={BadgeVariant.SUCCESS}>{i18n.common.active}</Badge>}
                            />
                            <DetailField
                                label={i18n.profile.authMethod}
                                value={<MutedText>{i18n.profile.authMethodDesc}</MutedText>}
                            />
                        </DetailList>
                    </CardContent>
                </Card>
            </DetailGrid>
        </SectionContainer>
    )
}
